import { Component, inject, Input, OnInit } from '@angular/core';
import { IonLabel } from '@ionic/angular/standalone';
import { ModeControlMultipleComponent } from '../../../../../../../../components/ecosystems/mode-control-multiple/mode-control-multiple.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SlideModalController } from '../../../../../../../slide-modal.service';
import { IProduct } from '../../../../../IProduct';
import {
  DeviceActionStackService,
  EdroNetworkManager,
} from '../../../../../../manager/edro-network-manager';
import { getTemperature } from '../../../../../../helper/temperature';
import { P00039DataRxTxIndex } from '../../../../P00039Sync';

@Component({
  selector: 'app-p00061-chauffage-controle',
  templateUrl: './p00061-chauffage-controle.component.html',
  styleUrls: ['./p00061-chauffage-controle.component.scss'],
  imports: [IonLabel, ModeControlMultipleComponent, TranslatePipe],
})
export class P00061ChauffageControleComponent implements OnInit {
  private slideModalcontroller: SlideModalController =
    inject(SlideModalController);
  private translateService: TranslateService = inject(TranslateService);

  @Input() productClass: IProduct | null = null;
  private edroNetworkManager: EdroNetworkManager = inject(EdroNetworkManager);

  public actionStack: DeviceActionStackService | null = null;

  public readonly minTemp = 15;
  public readonly maxTemp = 35;
  private isDragging = false;
  private tempValue = this.minTemp;

  constructor() {}

  ngOnInit() {
    this.actionStack = new DeviceActionStackService(
      this.productClass!,
      this.edroNetworkManager,
      null,
    );
  }

  // Obtient la température actuelle
  getCurrentTemp(): number {
    if (this.actionStack) {
      return (
        this.actionStack.getValue('wpPTemperatureConsigne') / 10 || this.minTemp
      );
    }
    return this.minTemp;
  }

  // Convertit la température en pourcentage de largeur
  getTempPercentage(): number {
    const temp = this.isDragging ? this.tempValue : this.getCurrentTemp();
    return ((temp - this.minTemp) / (this.maxTemp - this.minTemp)) * 100;
  }

  // Gestion du clic
  onClick(event: MouseEvent): void {
    if (!this.isDragging) {
      this.updateTempFromEvent(event);
      this.commitTempValue();
    }
  }

  // Début du drag
  onMouseDown(event: MouseEvent): void {
    this.isDragging = true;
    this.updateTempFromEvent(event);
    event.preventDefault();
  }

  // Mouvement pendant le drag
  onMouseMove(event: MouseEvent): void {
    if (this.isDragging) {
      this.updateTempFromEvent(event);
    }
  }

  // Fin du drag
  onMouseUp(event: MouseEvent): void {
    if (this.isDragging) {
      this.commitTempValue();
      this.isDragging = false;
    }
  }

  // Support tactile
  onTouchStart(event: TouchEvent): void {
    this.isDragging = true;
    this.updateTempFromTouch(event);
    event.preventDefault();
  }

  onTouchMove(event: TouchEvent): void {
    if (this.isDragging) {
      this.updateTempFromTouch(event);
      event.preventDefault();
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.isDragging) {
      this.commitTempValue();
      this.isDragging = false;
    }
  }

  private updateTempFromEvent(event: MouseEvent): void {
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const percentage = Math.max(
      0,
      Math.min(100, ((event.clientX - rect.left) / rect.width) * 100),
    );
    this.tempValue = Math.round(
      (percentage / 100) * (this.maxTemp - this.minTemp) + this.minTemp,
    );
  }

  private updateTempFromTouch(event: TouchEvent): void {
    const touch = event.touches[0];
    const rect = (event.currentTarget as HTMLElement).getBoundingClientRect();
    const percentage = Math.max(
      0,
      Math.min(100, ((touch.clientX - rect.left) / rect.width) * 100),
    );
    this.tempValue = Math.round(
      (percentage / 100) * (this.maxTemp - this.minTemp) + this.minTemp,
    );
  }

  // Valide et enregistre la nouvelle valeur de température
  private commitTempValue(): void {
    if (this.actionStack) {
      this.actionStack.editValue(
        P00039DataRxTxIndex.PACWP_Parametrage1,
        'wpPTemperatureConsigne',
        this.tempValue * 10,
      );
      this.actionStack.commit();
    }
  }

  // Obtient la valeur à afficher (temp en cours de modification ou actuelle)
  getDisplayTemp(): string {
    return this.isDragging
      ? getTemperature(this.tempValue, 0)
      : getTemperature(this.getCurrentTemp(), 0);
  }

  protected readonly P00039DataRxTxIndex = P00039DataRxTxIndex;
}
